import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { isValidEmailFormat } from '@shared/isValidEmailFormat';
import {
  selectSessionAdvisor,
  selectAdvisorHierarchyState,
  selectHierarchyInvitationMutationStatus,
  selectHierarchyDirectRelationshipMutationStatus,
} from '@client/model/selectors';
import {
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
  postAdvisorHierarchyInvitationByAdvisorIdRequest,
  postAdvisorHierarchyRelationshipDirectByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

export function useAdvisorHierarchyInvitation() {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { relationships, relationshipsFetchStatus } = useSelector(selectAdvisorHierarchyState);
  const { isMutating: isInviting, error: invitationError } = useSelector(
    selectHierarchyInvitationMutationStatus,
  );
  const { isMutating: isDirectAssigning, error: directAssignError } = useSelector(
    selectHierarchyDirectRelationshipMutationStatus,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [currentMutation, setCurrentMutation] = useState<'invite' | 'direct' | null>(null);

  const { toast } = useToast();
  const prevInvitingRef = useRef(false);
  const prevDirectAssigningRef = useRef(false);

  const advisorId = sessionAdvisor?.advisorId;

  useEffect(() => {
    if (advisorId && relationshipsFetchStatus === 'idle') {
      dispatch(fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({ advisorId }));
    }
  }, [dispatch, advisorId, relationshipsFetchStatus]);

  const handleMutationComplete = (
    error: string | null,
    successTitle: string,
    successDescription: string,
  ) => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        variant: 'destructive',
        className: '-translate-y-5',
      });
      setCurrentMutation(null);
    } else {
      toast({ title: successTitle, description: successDescription, className: '-translate-y-5' });
      setDialogOpen(false);
      setEmailError(null);
      setCurrentMutation(null);
      if (advisorId) {
        dispatch(fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({ advisorId }));
      }
    }
  };

  useEffect(() => {
    if (prevInvitingRef.current && !isInviting) {
      handleMutationComplete(
        invitationError,
        'Invitation Sent',
        'The invitation has been sent successfully.',
      );
    }
    prevInvitingRef.current = isInviting;
  }, [isInviting, invitationError, toast, advisorId, dispatch]);

  useEffect(() => {
    if (prevDirectAssigningRef.current && !isDirectAssigning) {
      handleMutationComplete(
        directAssignError,
        'Relationship Created',
        'The advisor has been added to the team successfully.',
      );
    }
    prevDirectAssigningRef.current = isDirectAssigning;
  }, [isDirectAssigning, directAssignError, toast, advisorId, dispatch]);

  const openDialog = () => {
    setDialogOpen(true);
    setEmailError(null);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEmailError(null);
  };

  const handleEmailChange = () => {
    if (emailError) {
      setEmailError(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    const normalizedEmail = email.toLowerCase().trim();

    if (!isValidEmailFormat(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }

    const existingRelationship = relationships?.find(
      relationship =>
        relationship.advisorEmail?.toLowerCase().trim() === normalizedEmail &&
        relationship.relationshipStatus !== 'inactive' &&
        relationship.relationshipStatus !== 'declined',
    );

    if (existingRelationship) {
      setEmailError('This advisor is already part of your team or has a pending invitation.');
      return false;
    }

    if (sessionAdvisor?.advisorEmail?.toLowerCase().trim() === normalizedEmail) {
      setEmailError('You cannot invite yourself to your own team.');
      return false;
    }

    setEmailError(null);
    return true;
  };

  const handleSendInvitation = (email: string, enabledClientAccess: boolean) => {
    const normalizedEmail = email.toLowerCase().trim();

    if (!advisorId) {
      setEmailError('Unable to send invitation. Please try again.');
      return;
    }

    setCurrentMutation('invite');
    dispatch(
      postAdvisorHierarchyInvitationByAdvisorIdRequest({
        advisorId,
        advisorEmail: normalizedEmail,
        isPreview: false,
        enabledClientAccess,
      }),
    );
  };

  const handleDirectAssign = (email: string, enabledClientAccess: boolean) => {
    const normalizedEmail = email.toLowerCase().trim();

    if (!advisorId) {
      setEmailError('Unable to create relationship. Please try again.');
      return;
    }

    setCurrentMutation('direct');
    dispatch(
      postAdvisorHierarchyRelationshipDirectByAdvisorIdRequest({
        advisorId,
        subordinateAdvisorEmail: normalizedEmail,
        enabledClientAccess,
      }),
    );
  };

  return {
    relationships,
    isMutating: isInviting || isDirectAssigning,
    currentMutation,
    dialogOpen,
    emailError,
    openDialog,
    closeDialog,
    handleEmailChange,
    validateEmail,
    handleSendInvitation,
    handleDirectAssign,
  };
}

export type UseAdvisorHierarchyInvitationReturn = ReturnType<typeof useAdvisorHierarchyInvitation>;
